/**
 * B2B Tiers Dashboard
 * Tabs: Customers | Tier Rules | SKU Overrides | Price Calculator
 *
 * Standalone — uses its own API client (services/api.b2b_tiers.ts) so this
 * component does not require any edit to services/api.ts. To wire into
 * the app, add a Route in App.tsx pointing /b2b-tiers/* at this default
 * export. (Per worktree HARD RULES, that one-line wire-up is a separate PR.)
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  b2bTiersApi,
  TierRule,
  TierPricingRule,
  CustomerTier,
  PriceQuote,
  ModuleStatus,
} from "../../services/api.b2b_tiers";

const C = {
  bg: "#060e20",
  card: "rgba(255,255,255,0.03)",
  border: "rgba(189,178,255,0.12)",
  accent: "#bdb2ff",
  text: "#e2e8f0",
  muted: "#64748b",
  success: "#22c55e",
  warning: "#fbbf24",
  danger: "#ef4444",
};

const glass: React.CSSProperties = {
  background: C.card,
  backdropFilter: "blur(60px)",
  WebkitBackdropFilter: "blur(60px)",
  border: `1px solid ${C.border}`,
  borderRadius: 18,
};

const TIER_COLORS: Record<string, string> = {
  retail: C.muted,
  wholesale: "#5cc7ff",
  dealer: "#fbbf24",
  distributor: "#a78bfa",
};

type TabKey = "customers" | "tiers" | "skus" | "calc";

export default function B2BTiersDashboard() {
  const [tab, setTab] = useState<TabKey>("customers");
  const [status, setStatus] = useState<ModuleStatus | null>(null);
  const [tierRules, setTierRules] = useState<TierRule[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([b2bTiersApi.status(), b2bTiersApi.listTierRules()])
      .then(([s, r]) => {
        setStatus(s);
        setTierRules(r);
      })
      .catch((e) => setError(String(e)));
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, padding: 24, fontFamily: "Manrope, system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <header style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 28, margin: 0 }}>B2B Tiers</h1>
          <p style={{ color: C.muted, marginTop: 4 }}>
            Buyer-side tier classification + tiered wholesale pricing.
          </p>
        </header>

        {error && (
          <div style={{ ...glass, padding: 16, marginBottom: 16, borderColor: C.danger, color: C.danger }}>
            {error}
          </div>
        )}

        <StatusBar status={status} />

        <Tabs current={tab} onChange={setTab} />

        <div style={{ ...glass, padding: 20, marginTop: 16 }}>
          {tab === "customers" && <CustomersTab tierRules={tierRules} />}
          {tab === "tiers" && (
            <TierRulesTab tierRules={tierRules} reload={(r) => setTierRules(r)} />
          )}
          {tab === "skus" && <SkuOverridesTab tierRules={tierRules} />}
          {tab === "calc" && <PriceCalculatorTab tierRules={tierRules} />}
        </div>
      </div>
    </div>
  );
}

function StatusBar({ status }: { status: ModuleStatus | null }) {
  if (!status) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
      <Card label="Tier rules" value={status.tier_rule_count} />
      <Card label="B2B customers" value={status.customer_tier_count} />
      <Card label="SKU overrides" value={status.sku_override_count} />
      <Card label="Module" value={`${status.module} v${status.version}`} mono />
    </div>
  );
}

function Card({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ ...glass, padding: 16 }}>
      <div style={{ color: C.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, fontFamily: mono ? "Space Grotesk, monospace" : undefined }}>
        {value}
      </div>
    </div>
  );
}

function Tabs({ current, onChange }: { current: TabKey; onChange: (t: TabKey) => void }) {
  const tabs: { key: TabKey; label: string }[] = [
    { key: "customers", label: "Customers" },
    { key: "tiers", label: "Tier rules" },
    { key: "skus", label: "SKU overrides" },
    { key: "calc", label: "Price calculator" },
  ];
  return (
    <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          style={{
            padding: "10px 16px",
            background: "transparent",
            border: "none",
            color: current === t.key ? C.accent : C.muted,
            borderBottom: `2px solid ${current === t.key ? C.accent : "transparent"}`,
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Customers tab ────────────────────────────────────────────────────────────

function CustomersTab({ tierRules }: { tierRules: TierRule[] }) {
  const [rows, setRows] = useState<CustomerTier[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reload() {
    setLoading(true);
    b2bTiersApi
      .listCustomerTiers(filter ? { tier_slug: filter } : {})
      .then((r) => { setRows(r); setError(null); })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(reload, [filter]);

  async function onOverride(leadId: string, tierSlug: string) {
    try {
      await b2bTiersApi.setLeadTier(leadId, tierSlug);
      reload();
    } catch (e) {
      setError(String(e));
    }
  }

  async function onReclassify(leadId: string) {
    try {
      await b2bTiersApi.reclassifyLead(leadId);
      reload();
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "center" }}>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8 }}
        >
          <option value="">All tiers</option>
          {tierRules.map((t) => (
            <option key={t.slug} value={t.slug}>
              {t.display_name}
            </option>
          ))}
        </select>
        <button onClick={reload} style={btnSecondary}>Refresh</button>
        {error && <span style={{ color: C.danger, fontSize: 13 }}>{error}</span>}
      </div>

      {loading ? (
        <div style={{ color: C.muted }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: C.muted, padding: 24, textAlign: "center" }}>
          No B2B-classified customers yet. Auto-classification fires when CRM publishes
          <code style={{ color: C.accent, margin: "0 4px" }}>lead.created</code>.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: C.muted, textAlign: "left", fontSize: 12, textTransform: "uppercase" }}>
              <th style={th}>Lead</th>
              <th style={th}>Tier</th>
              <th style={th}>Source</th>
              <th style={th}>Score</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={td}>
                    <code style={{ color: C.accent }}>{r.lead_id.slice(0, 8)}…</code>
                  </td>
                  <td style={td}>
                    <TierBadge slug={r.tier_slug} />
                  </td>
                  <td style={td}>
                    <span style={{ color: r.classification === "manual" ? C.warning : C.muted, fontSize: 12 }}>
                      {r.classification}
                    </span>
                  </td>
                  <td style={td}>{r.classifier_score?.toFixed(2) ?? "—"}</td>
                  <td style={td}>
                    <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} style={btnGhost}>
                      Why?
                    </button>
                    <select
                      onChange={(e) => e.target.value && onOverride(r.lead_id, e.target.value)}
                      style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: 4, marginLeft: 8 }}
                      value=""
                    >
                      <option value="">Override…</option>
                      {tierRules.map((t) => (
                        <option key={t.slug} value={t.slug}>{t.display_name}</option>
                      ))}
                    </select>
                    <button onClick={() => onReclassify(r.lead_id)} style={{ ...btnGhost, marginLeft: 8 }}>
                      Re-classify
                    </button>
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr>
                    <td colSpan={5} style={{ padding: 12, background: "rgba(189,178,255,0.04)" }}>
                      <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Classifier reasons:</div>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {r.classifier_reasons.map((reason, i) => (
                          <li key={i} style={{ fontSize: 13, color: C.text }}>{reason}</li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TierBadge({ slug }: { slug: string }) {
  const color = TIER_COLORS[slug] ?? C.muted;
  return (
    <span
      style={{
        background: `${color}20`,
        color,
        border: `1px solid ${color}40`,
        borderRadius: 6,
        padding: "2px 8px",
        fontSize: 12,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {slug}
    </span>
  );
}

// ── Tier rules tab ───────────────────────────────────────────────────────────

function TierRulesTab({ tierRules, reload }: { tierRules: TierRule[]; reload: (r: TierRule[]) => void }) {
  const [edits, setEdits] = useState<Record<string, Partial<TierRule>>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function update(slug: string, key: keyof TierRule, value: any) {
    setEdits((e) => ({ ...e, [slug]: { ...(e[slug] ?? {}), [key]: value } }));
  }

  async function save(slug: string) {
    setSaving(slug);
    try {
      await b2bTiersApi.updateTierRule(slug, edits[slug] ?? {});
      const fresh = await b2bTiersApi.listTierRules();
      reload(fresh);
      setEdits((e) => { const next = { ...e }; delete next[slug]; return next; });
      setError(null);
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(null);
    }
  }

  return (
    <div>
      {error && <div style={{ color: C.danger, marginBottom: 8 }}>{error}</div>}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ color: C.muted, textAlign: "left", fontSize: 12, textTransform: "uppercase" }}>
            <th style={th}>Tier</th>
            <th style={th}>Discount %</th>
            <th style={th}>Min monthly AED</th>
            <th style={th}>Min orders</th>
            <th style={th}>TRN required</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {tierRules.map((t) => {
            const e = edits[t.slug] ?? {};
            const dirty = Object.keys(e).length > 0;
            return (
              <tr key={t.slug} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={td}><TierBadge slug={t.slug} /> <span style={{ marginLeft: 8 }}>{t.display_name}</span></td>
                <td style={td}>
                  <input
                    type="number" step="0.5"
                    defaultValue={t.discount_pct}
                    onChange={(ev) => update(t.slug, "discount_pct", parseFloat(ev.target.value))}
                    style={inp}
                  />
                </td>
                <td style={td}>
                  <input
                    type="number"
                    defaultValue={t.min_monthly_aed}
                    onChange={(ev) => update(t.slug, "min_monthly_aed", parseFloat(ev.target.value))}
                    style={inp}
                  />
                </td>
                <td style={td}>
                  <input
                    type="number"
                    defaultValue={t.min_order_count}
                    onChange={(ev) => update(t.slug, "min_order_count", parseInt(ev.target.value))}
                    style={inp}
                  />
                </td>
                <td style={td}>
                  <input
                    type="checkbox"
                    defaultChecked={t.requires_trn}
                    onChange={(ev) => update(t.slug, "requires_trn", ev.target.checked)}
                  />
                </td>
                <td style={td}>
                  <button
                    disabled={!dirty || saving === t.slug}
                    onClick={() => save(t.slug)}
                    style={{ ...btnPrimary, opacity: dirty ? 1 : 0.4 }}
                  >
                    {saving === t.slug ? "Saving…" : "Save"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── SKU overrides tab ────────────────────────────────────────────────────────

function SkuOverridesTab({ tierRules }: { tierRules: TierRule[] }) {
  const [rows, setRows] = useState<TierPricingRule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({ sku: "", tier_slug: tierRules[0]?.slug ?? "wholesale", discount_pct: 10, fixed_price_aed: undefined as number | undefined });

  function reload() {
    b2bTiersApi.listSkuRules().then(setRows).catch((e) => setError(String(e)));
  }
  useEffect(reload, []);

  async function add() {
    if (!draft.sku) return;
    try {
      await b2bTiersApi.createSkuRule({
        sku: draft.sku,
        tier_slug: draft.tier_slug,
        discount_pct: draft.discount_pct,
        fixed_price_aed: draft.fixed_price_aed ?? null,
      });
      setDraft({ sku: "", tier_slug: draft.tier_slug, discount_pct: 10, fixed_price_aed: undefined });
      reload();
    } catch (e) { setError(String(e)); }
  }

  async function remove(id: string) {
    if (!confirm("Delete this override?")) return;
    try {
      await b2bTiersApi.deleteSkuRule(id);
      reload();
    } catch (e) { setError(String(e)); }
  }

  return (
    <div>
      {error && <div style={{ color: C.danger, marginBottom: 8 }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
        <input
          value={draft.sku}
          onChange={(e) => setDraft({ ...draft, sku: e.target.value })}
          placeholder="SKU"
          style={{ ...inp, width: 200 }}
        />
        <select
          value={draft.tier_slug}
          onChange={(e) => setDraft({ ...draft, tier_slug: e.target.value })}
          style={{ ...inp, width: 140 }}
        >
          {tierRules.map((t) => (
            <option key={t.slug} value={t.slug}>{t.display_name}</option>
          ))}
        </select>
        <input
          type="number" step="0.5"
          value={draft.discount_pct}
          onChange={(e) => setDraft({ ...draft, discount_pct: parseFloat(e.target.value) })}
          placeholder="Discount %"
          style={{ ...inp, width: 100 }}
        />
        <input
          type="number"
          value={draft.fixed_price_aed ?? ""}
          onChange={(e) => setDraft({ ...draft, fixed_price_aed: e.target.value ? parseFloat(e.target.value) : undefined })}
          placeholder="Fixed AED (optional)"
          style={{ ...inp, width: 160 }}
        />
        <button onClick={add} style={btnPrimary}>Add override</button>
      </div>

      {rows.length === 0 ? (
        <div style={{ color: C.muted, padding: 16, textAlign: "center" }}>
          No SKU overrides yet — tier defaults apply for every SKU.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: C.muted, textAlign: "left", fontSize: 12, textTransform: "uppercase" }}>
              <th style={th}>SKU</th>
              <th style={th}>Tier</th>
              <th style={th}>Discount %</th>
              <th style={th}>Fixed AED</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={td}><code style={{ color: C.accent }}>{r.sku}</code></td>
                <td style={td}><TierBadge slug={r.tier_slug} /></td>
                <td style={td}>{r.discount_pct}%</td>
                <td style={td}>{r.fixed_price_aed ?? "—"}</td>
                <td style={td}>
                  <button onClick={() => remove(r.id)} style={{ ...btnGhost, color: C.danger }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Price calculator tab ─────────────────────────────────────────────────────

function PriceCalculatorTab({ tierRules }: { tierRules: TierRule[] }) {
  const [sku, setSku] = useState("");
  const [retail, setRetail] = useState<number>(0);
  const [tier, setTier] = useState(tierRules[0]?.slug ?? "wholesale");
  const [quote, setQuote] = useState<PriceQuote | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function compute() {
    setError(null);
    try {
      const q = await b2bTiersApi.quoteForTier(sku, retail, tier);
      setQuote(q);
    } catch (e) { setError(String(e)); }
  }

  return (
    <div>
      <p style={{ color: C.muted, marginTop: 0 }}>
        Preview the final price for any SKU + tier combo. Useful when configuring
        SKU overrides — verify the discount stack before saving.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <input
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          placeholder="SKU"
          style={{ ...inp, width: 220 }}
        />
        <input
          type="number"
          value={retail}
          onChange={(e) => setRetail(parseFloat(e.target.value) || 0)}
          placeholder="Retail price AED"
          style={{ ...inp, width: 160 }}
        />
        <select value={tier} onChange={(e) => setTier(e.target.value)} style={{ ...inp, width: 140 }}>
          {tierRules.map((t) => (
            <option key={t.slug} value={t.slug}>{t.display_name}</option>
          ))}
        </select>
        <button onClick={compute} disabled={!sku || retail <= 0} style={btnPrimary}>Compute</button>
      </div>
      {error && <div style={{ color: C.danger, marginBottom: 8 }}>{error}</div>}
      {quote && (
        <div style={{ ...glass, padding: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
            <KV k="Retail" v={`AED ${quote.retail_price.toFixed(2)}`} />
            <KV k="Tier" v={<TierBadge slug={quote.tier_slug} />} />
            <KV k="Discount applied" v={`${quote.discount_pct}%`} />
            <KV
              k="Final price"
              v={
                <span style={{ color: C.success, fontSize: 20, fontWeight: 600 }}>
                  AED {quote.final_price_aed.toFixed(2)}
                </span>
              }
            />
            <KV k="Rule source" v={<code style={{ color: C.accent }}>{quote.rule_source}</code>} />
            {quote.fixed_price_aed != null && <KV k="Fixed override" v={`AED ${quote.fixed_price_aed.toFixed(2)}`} />}
          </div>
        </div>
      )}
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div style={{ color: C.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6 }}>{k}</div>
      <div style={{ marginTop: 2 }}>{v}</div>
    </div>
  );
}

// ── shared inline styles ─────────────────────────────────────────────────────

const th: React.CSSProperties = { padding: "8px 8px", fontSize: 11, fontWeight: 500 };
const td: React.CSSProperties = { padding: "10px 8px", fontSize: 14, verticalAlign: "middle" };
const inp: React.CSSProperties = {
  background: C.bg,
  color: C.text,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 14,
  outline: "none",
};
const btnPrimary: React.CSSProperties = {
  background: C.accent,
  color: C.bg,
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 13,
};
const btnSecondary: React.CSSProperties = {
  background: "transparent",
  color: C.text,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
  fontSize: 13,
};
const btnGhost: React.CSSProperties = {
  background: "transparent",
  color: C.muted,
  border: "none",
  cursor: "pointer",
  fontSize: 12,
  padding: 4,
};
